import { isSameDay, isSameMonth, isThursday, nextThursday } from 'date-fns';
import type { PettyCashReceipt } from '@elka/types';
import { toDate, type TimestampLike } from './date';

type Amounts = Pick<PettyCashReceipt, 'amount' | 'approvedAmount'>;
type Summarizable = Amounts & Pick<PettyCashReceipt, 'status'> & { paidAt?: TimestampLike };
type WorkerReceipt = Summarizable & Pick<PettyCashReceipt, 'workerId' | 'workerName'>;

/** Receipts are a photo or a PDF; the upload keeps the file extension in the Storage path. */
export const isPdfReceipt = (photoPath: string) => /\.pdf$/i.test(photoPath);

/** What the worker gets back — the manager's corrected amount when there is one. */
export const shownAmount = (r: Amounts) => r.approvedAmount ?? r.amount;

export const isCorrected = (r: Amounts) =>
  r.approvedAmount != null && r.approvedAmount !== r.amount;

export function summarize(receipts: Summarizable[], now = new Date()) {
  let owed = 0;
  let pending = 0;
  let pendingCount = 0;
  let paidThisMonth = 0;
  for (const r of receipts) {
    if (r.status === 'approved') owed += shownAmount(r);
    else if (r.status === 'pending') {
      pending += r.amount;
      pendingCount++;
    } else if (r.status === 'paid') {
      const paidAt = toDate(r.paidAt);
      if (paidAt && isSameMonth(paidAt, now)) paidThisMonth += shownAmount(r);
    }
  }
  return { owed, pending, pendingCount, paidThisMonth };
}

/** A receipt marked paid today — the ones a payout undo puts back to approved. */
export const isPaidOn = (r: Pick<Summarizable, 'status' | 'paidAt'>, day: Date) => {
  const paidAt = toDate(r.paidAt);
  return r.status === 'paid' && !!paidAt && isSameDay(paidAt, day);
};

/**
 * Per-worker balances for the manager page, sorted by name. `paidToday` is the
 * payout already marked today; a receipt approved after that waits for the next one.
 */
export function workerBalances(receipts: WorkerReceipt[], now = new Date()) {
  const byWorker = new Map<string, WorkerReceipt[]>();
  for (const r of receipts) byWorker.set(r.workerId, [...(byWorker.get(r.workerId) ?? []), r]);

  return [...byWorker.values()]
    .map((rs) => {
      let lastPaidAt: Date | null = null;
      let paidToday = 0;
      for (const r of rs) {
        const paidAt = r.status === 'paid' ? toDate(r.paidAt) : null;
        if (paidAt && (!lastPaidAt || paidAt > lastPaidAt)) lastPaidAt = paidAt;
        if (isPaidOn(r, now)) paidToday += shownAmount(r);
      }
      return {
        workerId: rs[0].workerId,
        workerName: rs[0].workerName,
        ...summarize(rs, now),
        owedCount: rs.filter((r) => r.status === 'approved').length,
        paidToday,
        paidTodayCount: rs.filter((r) => isPaidOn(r, now)).length,
        lastPaidAt,
      };
    })
    .sort((a, b) => a.workerName.localeCompare(b.workerName, 'he'));
}

export type WorkerBalance = ReturnType<typeof workerBalances>[number];

/** Payouts go out on Thursdays: today if it is one, else the coming Thursday. */
export const nextPayday = (now = new Date()) => (isThursday(now) ? now : nextThursday(now));

/** ₪1,240 / ₪186.50 — agorot only when there are any. */
export function formatShekel(n: number): string {
  const v = Math.round(n * 100) / 100;
  const digits = v % 1 ? 2 : 0;
  return `₪${v.toLocaleString('he-IL', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}
